import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface PortfolioContent {
  id: string;
  portfolio_code: string;
  title: string;
  subtitle?: string | null;
  description: string;
  key_characteristics: string[];
  expected_annual_return?: string | null;
  expected_volatility?: string | null;
  sectoral_allocation?: string | null;
  target_audience?: string | null;
  allocation_summary?: string | null;
  rebalancing_frequency?: string | null;
  risk_profile?: string | null;
  allocation_processing_rules?: Record<string, any> | null;
  status: string;
}

export interface PortfolioBenchmark {
  id: string;
  portfolio_code: string;
  benchmark_type: string;
  benchmark_name: string;
  benchmark_short_name?: string | null;
  display_order: number;
  show_excess_calculation: boolean;
  excess_label?: string | null;
}

export const usePortfolioContent = (portfolioCode: string) => {
  const [content, setContent] = useState<PortfolioContent | null>(null);
  const [benchmarks, setBenchmarks] = useState<PortfolioBenchmark[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPortfolioData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch portfolio content
        const { data: contentData, error: contentError } = await supabase
          .from('portfolio_content')
          .select('*')
          .eq('portfolio_code', portfolioCode)
          .eq('status', 'active')
          .single();

        if (contentError) {
          throw new Error(`Failed to fetch content: ${contentError.message}`);
        }

        // Fetch portfolio benchmarks
        const { data: benchmarkData, error: benchmarkError } = await supabase
          .from('portfolio_benchmarks')
          .select('*')
          .eq('portfolio_code', portfolioCode)
          .order('display_order');

        if (benchmarkError) {
          throw new Error(`Failed to fetch benchmarks: ${benchmarkError.message}`);
        }

        // Transform the data to match our interfaces
        const transformedContent: PortfolioContent = {
          ...contentData,
          key_characteristics: Array.isArray(contentData.key_characteristics) 
            ? contentData.key_characteristics as string[]
            : [],
          allocation_processing_rules: contentData.allocation_processing_rules 
            ? contentData.allocation_processing_rules as Record<string, any>
            : null
        };

        const transformedBenchmarks: PortfolioBenchmark[] = (benchmarkData || []).map(benchmark => ({
          ...benchmark,
          benchmark_type: benchmark.benchmark_type as string
        }));

        setContent(transformedContent);
        setBenchmarks(transformedBenchmarks);
      } catch (err) {
        console.error('Error fetching portfolio data:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch portfolio data');
        setContent(null);
        setBenchmarks([]);
      } finally {
        setLoading(false);
      }
    };

    if (portfolioCode) {
      fetchPortfolioData();
    }
  }, [portfolioCode]);

  return { content, benchmarks, loading, error };
};